import { motion } from "framer-motion";
import { Gift, CalendarDays, LayoutGrid, Clapperboard, PenTool, Plane, Users, Megaphone, ArrowUpRight } from "lucide-react";

const SERVICES = [
  {
    icon: Gift,
    title: "Corporate Gifting & Merchandise",
    desc: "Premium gifts, executive hampers, employee kits, trophies, uniforms and branded merchandise.",
  },
  {
    icon: CalendarDays,
    title: "Corporate Events & Experiences",
    desc: "Conferences, roundtables, awards, AGMs, launches, gala dinners, venue and event production.",
  },
  {
    icon: LayoutGrid,
    title: "Exhibition & Expo Solutions",
    desc: "Stall design and fabrication, exhibitor branding, booth support, signage and lead capture.",
  },
  {
    icon: Clapperboard,
    title: "Corporate Media & Content",
    desc: "Corporate films, leadership interviews, podcasts, photography, publications and reports.",
  },
  {
    icon: PenTool,
    title: "Branding, Creative & Printing",
    desc: "Brand identity, event creative, pitch decks, brochures, signage and print production.",
  },
  {
    icon: Plane,
    title: "Corporate Travel & Destination Management",
    desc: "Group travel, hotel bookings, transfers, destination events, retreats and offsites.",
  },
  {
    icon: Users,
    title: "Employee Engagement & Internal Communications",
    desc: "Town halls, employee recognition, team-building, internal campaigns and celebrations.",
  },
  {
    icon: Megaphone,
    title: "Brand Activations & Experiential Marketing",
    desc: "Roadshows, product demonstrations, interactive brand experiences and on-ground campaigns.",
  },
];

const preselect = (title) => {
  window.dispatchEvent(new CustomEvent("oec:preselect", { detail: title }));
};

export default function ServicesGrid() {
  return (
    <section id="services" className="relative py-20 sm:py-28 scroll-mt-20">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="max-w-3xl">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6 }}
            className="font-mono text-[11px] sm:text-xs tracking-[0.3em] uppercase text-gold-dark"
          >
            What we do
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6, delay: 0.08 }}
            className="mt-4 font-display text-3xl sm:text-5xl font-bold tracking-tight text-royal-deep leading-[1.1]"
          >
            Everything you need.{" "}
            <span className="text-stroke">One connected partner.</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6, delay: 0.16 }}
            className="mt-5 text-sm sm:text-lg text-black/70 leading-relaxed"
          >
            Choose one service or bring us a broader brief. We coordinate the right expertise,
            suppliers and execution for your business objectives.
          </motion.p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-4">
          {SERVICES.map((s, i) => (
            <motion.article
              key={s.title}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.6, delay: (i % 2) * 0.08, ease: [0.22, 1, 0.36, 1] }}
              data-testid="service-card"
              className="group relative card-sheen rounded-3xl border border-black/10 bg-white p-7 sm:p-9 transition-all duration-400 hover:border-gold/50 hover:shadow-[0_18px_50px_rgba(224,19,39,0.14)]"
            >
              <div className="flex items-start justify-between">
                <span className="grid h-12 w-12 place-items-center rounded-2xl border border-gold/40 bg-gold-soft text-gold-dark transition-all duration-300 group-hover:bg-gold group-hover:text-white group-hover:shadow-[0_0_24px_rgba(224,19,39,0.4)]">
                  <s.icon size={20} />
                </span>
                <span className="font-mono text-xs text-black/30 group-hover:text-gold-dark transition-colors duration-300">
                  {String(i + 1).padStart(2, "0")}
                </span>
              </div>
              <h3 className="mt-6 font-display text-xl sm:text-2xl font-semibold tracking-tight text-royal-deep leading-snug">
                {s.title}
              </h3>
              <p className="mt-3 text-sm text-black/65 leading-relaxed">{s.desc}</p>
              <a
                href="#enquiry"
                onClick={() => preselect(s.title)}
                data-testid={`service-discuss-${i + 1}`}
                className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-black/70 transition-colors duration-300 hover:text-gold-dark"
              >
                Discuss this service
                <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
